export const SHIFT_DAY_OPTIONS = [
  { code: "sun", label: "Sun." },
  { code: "mon", label: "Mon." },
  { code: "tue", label: "Tue." },
  { code: "wed", label: "Wed." },
  { code: "thu", label: "Thu." },
  { code: "fri", label: "Fri." },
  { code: "sat", label: "Sat." },
];

export const DEFAULT_WEEKDAY_CODES = ["mon", "tue", "wed", "thu", "fri"];

const DAY_LABEL_BY_CODE = Object.fromEntries(
  SHIFT_DAY_OPTIONS.map((d) => [d.code, d.label])
);

const DAY_ORDER = SHIFT_DAY_OPTIONS.map((d) => d.code);

export const sortDayCodes = (days) => {
  if (!Array.isArray(days)) return [];
  const order = Object.fromEntries(DAY_ORDER.map((c, i) => [c, i]));
  return [...days].sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99));
};

export const formatDaysSummary = (days) => {
  const sorted = sortDayCodes(days);
  if (!sorted.length) return "—";
  if (sorted.length === 7) return "Sun–Sat";

  const indices = sorted.map((c) => DAY_ORDER.indexOf(c)).filter((i) => i >= 0);
  const isContiguous =
    indices.length > 1 && indices[indices.length - 1] - indices[0] === indices.length - 1;

  if (isContiguous && indices.length >= 2) {
    return `${DAY_LABEL_BY_CODE[sorted[0]]}–${DAY_LABEL_BY_CODE[sorted[sorted.length - 1]]}`;
  }

  return sorted.map((c) => DAY_LABEL_BY_CODE[c] || c).join(", ");
};

export const formatDaysList = (days) => formatDaysSummary(days);

/** Minutes since midnight; 24:00 => 1440 */
export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const t = String(timeStr).trim();
  if (t === "24:00") return 24 * 60;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** 0–100 for timeline positioning */
export const timeToPercent = (timeStr) => (timeToMinutes(timeStr) / (24 * 60)) * 100;

export const getShiftBarStyle = (startTime, endTime) => {
  const left = timeToPercent(startTime);
  const right = timeToPercent(endTime);
  const width = Math.max(0, right - left);
  return { left: `${left}%`, width: `${width}%` };
};

export const HOUR_TICKS = Array.from({ length: 13 }, (_, i) => i * 2);

export const formatMinutesLabel = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '' || minutes === '0' || minutes === 0) {
    return '-';
  }
  const n = Number(minutes) || 0;
  if (n < 60) return `${n}min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
};

export const minutesToTimeString = (totalMinutes) => {
  if (totalMinutes >= 24 * 60) return "24:00";
  const capped = Math.max(0, Math.min(totalMinutes, 24 * 60 - 1));
  const h = Math.floor(capped / 60);
  const m = capped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const suggestPolicyDefaults = (startTime, endTime, breakMinutes = 0) => {
  const startMins = timeToMinutes(startTime);
  const endMins = timeToMinutes(endTime);
  const scheduled = Math.max(0, endMins - startMins);
  const breakMins = Math.max(0, Number(breakMinutes) || 0);

  return {
    late_allowed_minutes: 0,
    early_leave_allowed_minutes: 0,
    break_duration_minutes: breakMins,
    min_work_minutes: Math.max(1, scheduled - breakMins),
    check_in_start: startTime,
    check_in_end: minutesToTimeString(Math.min(startMins + 120, 24 * 60 - 1)),
    check_out_start: endTime === "24:00" ? "23:59" : endTime,
    check_out_end: minutesToTimeString(Math.min(endMins + 360, 24 * 60)),
  };
};

export const getWindowBarStyle = (startTime, endTime) =>
  getShiftBarStyle(startTime, endTime);

export const DEFAULT_POLICY_FORM = () =>
  suggestPolicyDefaults("08:00", "17:00", 0);

export const isFullDaySchedule = (startTime, endTime) =>
  startTime === "00:00" && endTime === "24:00";

export const createDefaultDaySchedule = () => {
  const start_time = "08:00";
  const end_time = "17:00";
  return {
    start_time,
    end_time,
    is_full_day: false,
    check_out_end_24: false,
    ...suggestPolicyDefaults(start_time, end_time, 0),
  };
};

export const dayScheduleFromApiEntry = (entry) => {
  const start_time = entry?.start_time || "08:00";
  const end_time = entry?.end_time || "17:00";
  const check_out_end = entry?.check_out_end || "23:28";
  return {
    start_time,
    end_time,
    is_full_day: isFullDaySchedule(start_time, end_time),
    late_allowed_minutes: entry?.late_allowed_minutes ?? 0,
    early_leave_allowed_minutes: entry?.early_leave_allowed_minutes ?? 0,
    break_duration_minutes: entry?.break_duration_minutes ?? 0,
    min_work_minutes: entry?.min_work_minutes ?? 480,
    check_in_start: entry?.check_in_start ?? "06:00",
    check_in_end: entry?.check_in_end ?? "10:00",
    check_out_start: entry?.check_out_start ?? end_time,
    check_out_end,
    check_out_end_24: check_out_end === "24:00",
  };
};

/** Expand API shift (legacy flat or day_schedules) into a map keyed by day code. */
export const expandShiftToDayScheduleMap = (shift) => {
  const map = {};
  if (Array.isArray(shift?.day_schedules) && shift.day_schedules.length) {
    shift.day_schedules.forEach((entry) => {
      if (entry?.day) {
        map[entry.day] = dayScheduleFromApiEntry(entry);
      }
    });
    return map;
  }
  const days = sortDayCodes(shift?.days?.length ? shift.days : DEFAULT_WEEKDAY_CODES);
  const flat = dayScheduleFromApiEntry({
    start_time: shift?.start_time,
    end_time: shift?.end_time,
    late_allowed_minutes: shift?.late_allowed_minutes,
    early_leave_allowed_minutes: shift?.early_leave_allowed_minutes,
    break_duration_minutes: shift?.break_duration_minutes,
    min_work_minutes: shift?.min_work_minutes,
    check_in_start: shift?.check_in_start,
    check_in_end: shift?.check_in_end,
    check_out_start: shift?.check_out_start,
    check_out_end: shift?.check_out_end,
  });
  days.forEach((day) => {
    map[day] = { ...flat };
  });
  return map;
};

export const cloneDaySchedule = (schedule) => ({
  ...(schedule || createDefaultDaySchedule()),
});

export const buildDaySchedulesPayload = (selectedDays, schedulesByDay) =>
  sortDayCodes(selectedDays).map((day) => {
    const s = schedulesByDay[day] || createDefaultDaySchedule();
    const start_time = s.is_full_day ? "00:00" : s.start_time;
    const end_time = s.is_full_day ? "24:00" : s.end_time;
    return {
      day,
      start_time,
      end_time,
      late_allowed_minutes: Number(s.late_allowed_minutes) || 0,
      early_leave_allowed_minutes: Number(s.early_leave_allowed_minutes) || 0,
      break_duration_minutes: Number(s.break_duration_minutes) || 0,
      min_work_minutes: Number(s.min_work_minutes) || 1,
      check_in_start: s.check_in_start,
      check_in_end: s.check_in_end,
      check_out_start: s.check_out_start,
      check_out_end: s.check_out_end_24 ? "24:00" : s.check_out_end,
    };
  });

export const getScheduleForDay = (shift, dayCode) => {
  if (Array.isArray(shift?.day_schedules) && shift.day_schedules.length) {
    const found = shift.day_schedules.find((s) => s.day === dayCode);
    if (found) return found;
  }
  return {
    day: dayCode,
    start_time: shift?.start_time,
    end_time: shift?.end_time,
    late_allowed_minutes: shift?.late_allowed_minutes,
    early_leave_allowed_minutes: shift?.early_leave_allowed_minutes,
    break_duration_minutes: shift?.break_duration_minutes,
    min_work_minutes: shift?.min_work_minutes,
    check_in_start: shift?.check_in_start,
    check_in_end: shift?.check_in_end,
    check_out_start: shift?.check_out_start,
    check_out_end: shift?.check_out_end,
  };
};

export const formatShiftTimesSummary = (shift) => {
  const schedules =
    Array.isArray(shift?.day_schedules) && shift.day_schedules.length
      ? sortDayCodes(shift.day_schedules.map((s) => s.day)).map((day) =>
          shift.day_schedules.find((s) => s.day === day)
        )
      : null;

  if (!schedules || !schedules.length) {
    if (isFullDaySchedule(shift?.start_time, shift?.end_time)) {
      return "00:00 – 24:00 (full day)";
    }
    return `${shift?.start_time || "—"} – ${shift?.end_time || "—"}`;
  }

  const first = schedules[0];
  const allSame = schedules.every(
    (s) => s.start_time === first.start_time && s.end_time === first.end_time
  );
  if (allSame) {
    if (isFullDaySchedule(first.start_time, first.end_time)) {
      return "00:00 – 24:00 (full day)";
    }
    return `${first.start_time} – ${first.end_time}`;
  }

  return schedules
    .map((s) => {
      const label = (DAY_LABEL_BY_CODE[s.day] || s.day).replace(/\.$/, "");
      if (isFullDaySchedule(s.start_time, s.end_time)) {
        return `${label} full day`;
      }
      return `${label} ${s.start_time}–${s.end_time}`;
    })
    .join(", ");
};
